import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const LAST_UPDATED = "February 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="privacy-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="privacy-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <div className="label-mono">Privacy</div>
        </div>
      </header>

      {/* Body */}
      <article className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="label-mono mb-6">
          <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
          Legal / Privacy Policy
        </div>
        <h1 className="font-heading font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
          Privacy Policy
        </h1>
        <div className="mt-4 text-sm text-neutral-500 font-mono uppercase tracking-widest">Last updated · {LAST_UPDATED}</div>

        <div className="mt-6 border border-black/10 p-5 flex items-start gap-3 bg-neutral-50">
          <ShieldCheck size={20} weight="duotone" className="text-[#2400FF] shrink-0 mt-0.5" />
          <p className="text-sm text-neutral-700 leading-relaxed">
            <b>Short version:</b> MyResumeDesign does not create user accounts and does not store your résumé
            data in any database. Your information exists only in your active browser session and is wiped
            when you close the tab.
          </p>
        </div>

        <div className="prose-legal mt-10 space-y-8 text-neutral-800 leading-relaxed">

          <Section n="1" title="Information We Do Not Collect">
            <p>
              We do not require registration. We do not ask for your name, email, or phone number to use the
              service. We do not create user accounts, tracking profiles, or long-term identifiers. We do not
              set advertising or cross-site tracking cookies.
            </p>
          </Section>

          <Section n="2" title="Information You Enter or Upload">
            <p>
              When you fill in the résumé builder or upload an existing résumé file, the content is held in
              your browser's memory (React application state) for the duration of your session. This content is
              not persisted to any server database, cloud storage, or third-party service maintained by us.
            </p>
            <p>
              When you close the tab, refresh the page, or navigate away, this session state is discarded and
              cannot be recovered by us. If you want to keep a copy, download the PDF before leaving.
            </p>
          </Section>

          <Section n="3" title="AI Polish and Job-Description Matching">
            <p>
              When you tap the AI polish button on a bullet or Summary, or use the Match JD feature, the
              specific text you have chosen to polish (and, for JD matching, the résumé structure plus the job
              description you pasted) is sent to a large language model provider — Anthropic (Claude), accessed
              through Emergent — to generate a rewrite or a match analysis.
            </p>
            <p>
              These requests are transient. We do not retain the request payloads or the model outputs beyond
              the immediate HTTP response used to update the preview. Please review the
              relevant provider's privacy notice for their handling of API traffic: Anthropic
              (<a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer" className="underline">anthropic.com/legal/privacy</a>).
            </p>
          </Section>

          <Section n="4" title="Résumé File Upload">
            <p>
              The Import feature accepts .pdf and .docx files. The file is transmitted to our server only for
              the duration of parsing (extracting text into structured fields) and is not stored on disk after
              the request completes. Only the extracted text — not the original file — is returned to your
              browser session.
            </p>
          </Section>

          <Section n="5" title="Payments">
            <p>
              If a paid PDF download is offered, the payment is processed by Razorpay. When you initiate a
              payment, we create an order (currency amount and a unique order ID) with Razorpay and their SDK
              collects your card or UPI details directly. We never see or store your card number, CVV, or bank
              credentials. We do retain the order ID and payment ID returned by Razorpay for the duration of
              the session to verify the payment before unlocking the download.
            </p>
            <p>
              Razorpay's privacy policy governs their handling of payment data:
              (<a href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer" className="underline">razorpay.com/privacy</a>).
            </p>
          </Section>

          <Section n="6" title="Cookies and Local Storage">
            <p>
              We use a small amount of browser session storage to remember non-personal preferences (e.g., a
              flag indicating you have acknowledged the session-only privacy notice) so we don't repeatedly
              interrupt you. This data lives on your device only. We do not set any tracking, advertising,
              analytics, or fingerprinting cookies.
            </p>
          </Section>

          <Section n="7" title="Logs and Analytics">
            <p>
              Our hosting infrastructure may retain standard server access logs (timestamp, IP address, request
              path, response status) for a short period, exclusively for security, abuse-detection and
              debugging purposes. These logs are not associated with the résumé content you enter.
            </p>
          </Section>

          <Section n="8" title="Data You Choose to Download">
            <p>
              When you click Download PDF, the résumé is rendered inside your browser and passed to your
              operating system's print pipeline (typically "Save as PDF"). We do not receive a copy of the
              resulting PDF file. The saved file lives only on your local machine.
            </p>
          </Section>

          <Section n="9" title="Children">
            <p>
              MyResumeDesign is intended for adults preparing job applications. It is not directed at children
              under 16 and we do not knowingly collect information from children.
            </p>
          </Section>

          <Section n="10" title="Changes to This Policy">
            <p>
              We may update this policy from time to time to reflect changes to the tool. Material changes will
              be indicated by an updated "Last updated" date at the top of this page. Continued use after an
              update constitutes acceptance of the revised policy.
            </p>
          </Section>

          <Section n="11" title="Contact">
            <p>
              Because we hold no personal data about you, there is generally nothing for us to delete or
              export. For any privacy-related question, write to
              {" "}<a href="mailto:contact@myresumedesign.com" className="underline hover:text-black">contact@myresumedesign.com</a>.
            </p>
          </Section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono">© 2026 MyResumeDesign</div>
          <div className="flex gap-6 font-mono uppercase tracking-widest text-xs">
            <Link to="/help" className="hover:text-black">Help</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-xs text-neutral-400">§ {n}</span>
        <h2 className="font-heading font-bold text-xl tracking-tight">{title}</h2>
      </div>
      <div className="space-y-3 text-sm">{children}</div>
    </section>
  );
}
